import { type ICartItemGroup } from 'ecosystem'
import { useCallback, useMemo } from 'react'
import { HStack, Stack, VStack } from '@chakra-ui/react'
import { GrandIconButton, GrandText, ImageBox, Price } from 'ui'
import { MdRemove } from 'react-icons/md'
import { getProductOriginalPrice, getProductPrice } from 'shared-utils'
import { useVatMode } from '../../../../../common/hooks/useVatMode'
import type { CheckoutCartItemProps } from '../types'
import { CheckoutCartItemCounter } from '../CheckoutCartItemCounter'
import { CheckoutCartItemGroupDetail } from './CheckoutCartItemGroupDetail'

export type CheckoutCartItemGroupProps = Omit<CheckoutCartItemProps, 'cartItem'> & {
  cartItem: ICartItemGroup
}

export const CheckoutCartItemGroup = ({
  cartItem,
  onServicesIdsUpdate,
  onDelete,
  onUpdate,
  isEditable = true
}: CheckoutCartItemGroupProps) => {
  const qty = cartItem.qty || 1
  const { imgUrl, name, items } = cartItem.meta.groupType
  const { excludeVat } = useVatMode()

  const onQtyChange = useCallback(
    (newQty: number) => {
      onUpdate({
        ...cartItem,
        qty: newQty
      })
    },
    [cartItem, onUpdate]
  )

  const { total, totalOriginal } = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        return {
          total: acc.total + getProductPrice(item.product, excludeVat) * (item.originalQty ?? 1),
          totalOriginal:
            acc.totalOriginal +
            getProductOriginalPrice(item.product, excludeVat) * (item.originalQty ?? 1)
        }
      },
      { total: 0, totalOriginal: 0 }
    )
  }, [items, excludeVat])

  return (
    <VStack borderColor="gray.100" borderWidth={1} p={4} gap={4} w="100%">
      <Stack direction={{ base: 'column', sm: 'row' }} w="100%">
        <GrandText display={{ base: 'initial', sm: 'none' }} fontSize="sm">
          {name}
        </GrandText>

        <HStack spacing={4} w="full" pt={imgUrl ? 0 : 4}>
          {!!imgUrl && (
            <ImageBox
              alignSelf="flex-start"
              imageProps={{
                alt: name,
                title: name,
                src: imgUrl,
                style: {
                  objectFit: 'contain'
                },
                sizes: '7em'
              }}
              w={{
                base: 10,
                md: 12
              }}
              h={{
                base: 10,
                md: 12
              }}
            />
          )}

          <VStack flex={1} w="full">
            <HStack
              justifyContent={{
                base: 'flex-end',
                sm: 'initial'
              }}
              w="100%"
            >
              <GrandText display={{ base: 'none', sm: 'initial' }} flexGrow={1} w="auto">
                {name}
              </GrandText>

              <HStack align="center">
                <Price
                  price={total}
                  originalPrice={totalOriginal}
                  priceProps={{
                    color: 'inherit'
                  }}
                  alignItems="baseline"
                  whiteSpace="nowrap"
                  mr={2}
                />

                {isEditable ? (
                  <CheckoutCartItemCounter
                    onChange={(_valueAsString, valueAsNumber) => {
                      onQtyChange(valueAsNumber)
                    }}
                    value={cartItem.qty}
                    min={1}
                  />
                ) : (
                  <GrandText fontSize="xs" w="auto">
                    {qty}x
                  </GrandText>
                )}

                <GrandIconButton
                  aria-label="Ta bort"
                  bg="status.error"
                  color="text.light"
                  flexShrink={0}
                  fontSize="xs"
                  h={4}
                  icon={<MdRemove />}
                  onClick={() => {
                    onDelete(cartItem)
                  }}
                  shadow="none"
                  size="xs"
                  tooltip="Ta bort"
                  w={4}
                />
              </HStack>
            </HStack>
          </VStack>
        </HStack>
      </Stack>

      {/* Detail */}
      {items.map((detailItem) => (
        <CheckoutCartItemGroupDetail
          key={detailItem.product.id}
          item={detailItem}
          onServicesIdsUpdate={onServicesIdsUpdate}
        />
      ))}
    </VStack>
  )
}
